/**
 * VaultPage — private, passcode-gated Founder Vault (June 2026).
 *
 * The founder's personal repository for notes, links, and uploaded files.
 * Strictly isolated from clinical data: backend reads/writes only the
 * `founder_resources` collection + Emergent object storage. Gated by
 * VAULT_PASSCODE (default "sivam"). Not linked from any public surface
 * except a discreet footer entry on the home page.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Lock, Unlock, FileText, Link2, StickyNote, Upload, Trash2,
  ExternalLink, Download, ShieldCheck, BookText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
  note: { icon: StickyNote, label: 'Note', color: 'text-amber-600' },
  link: { icon: Link2, label: 'Link', color: 'text-blue-600' },
  file: { icon: FileText, label: 'File', color: 'text-emerald-600' },
};

function humanSize(bytes) {
  if (!bytes) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function VaultPage() {
  const [passcode, setPasscode] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  // add-forms state
  const [tab, setTab] = useState('note');
  const [noteForm, setNoteForm] = useState({ title: '', content: '', tags: '' });
  const [linkForm, setLinkForm] = useState({ title: '', url: '', tags: '' });
  const [fileForm, setFileForm] = useState({ title: '', tags: '' });
  const [fileObj, setFileObj] = useState(null);
  const [adding, setAdding] = useState(false);

  const loadItems = useCallback(async (pc) => {
    const r = await axios.post(`${API}/vault/items`, { passcode: pc });
    setItems(r.data.items || []);
  }, []);

  const tryUnlock = async () => {
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/vault/auth`, { passcode });
      await loadItems(passcode);
      setUnlocked(true);
    } catch (e) {
      setError(e?.response?.status === 401 ? 'Invalid passcode.' : 'Could not unlock the vault.');
    } finally { setBusy(false); }
  };

  const lock = () => { setUnlocked(false); setPasscode(''); setItems([]); setError(''); };

  const addNote = async () => {
    if (!noteForm.title.trim()) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/vault/note`, {
        passcode, title: noteForm.title, content: noteForm.content,
        tags: noteForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setNoteForm({ title: '', content: '', tags: '' });
      await loadItems(passcode);
    } catch (e) { setError('Could not save note.'); } finally { setAdding(false); }
  };

  const addLink = async () => {
    if (!linkForm.title.trim() || !linkForm.url.trim()) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/vault/link`, {
        passcode, title: linkForm.title, url: linkForm.url,
        tags: linkForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setLinkForm({ title: '', url: '', tags: '' });
      await loadItems(passcode);
    } catch (e) { setError('Could not save link.'); } finally { setAdding(false); }
  };

  const uploadFile = async () => {
    if (!fileObj) return;
    setAdding(true); setError('');
    try {
      const fd = new FormData();
      fd.append('passcode', passcode);
      fd.append('title', fileForm.title);
      fd.append('tags', fileForm.tags);
      fd.append('file', fileObj);
      await axios.post(`${API}/vault/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFileForm({ title: '', tags: '' }); setFileObj(null);
      await loadItems(passcode);
    } catch (e) {
      setError(e?.response?.status === 413 ? 'File too large (25 MB max).' : 'Upload failed.');
    } finally { setAdding(false); }
  };

  const openFile = async (item) => {
    try {
      const r = await axios.get(`${API}/vault/file/${item.id}`, {
        params: { passcode }, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError('Could not open file.'); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/vault/item/${item.id}`, { params: { passcode } });
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (e) { setError('Could not delete item.'); }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4" data-testid="vault-lock-screen">
        <div className="w-full max-w-sm bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <h1 className="text-lg font-bold text-white">Founder Vault</h1>
          </div>
          <p className="text-xs text-slate-400 mb-6">Private repository — notes, links &amp; files. Passcode required.</p>
          <Input
            type="password"
            placeholder="Vault passcode"
            value={passcode}
            onChange={e => setPasscode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && passcode && tryUnlock()}
            className="bg-slate-900 border-slate-600 text-white mb-3"
            data-testid="vault-passcode-input"
            autoComplete="off"
          />
          <Button
            onClick={tryUnlock}
            disabled={!passcode || busy}
            className="w-full bg-emerald-600 hover:bg-emerald-500"
            data-testid="vault-unlock-btn"
          >
            <Lock className="w-4 h-4 mr-2" /> {busy ? 'Unlocking…' : 'Unlock Vault'}
          </Button>
          {error && <p className="mt-3 text-xs text-red-400" data-testid="vault-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vault-dashboard">
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-xl font-bold">Founder Vault</h1>
            <p className="text-xs text-slate-400">{items.length} item{items.length === 1 ? '' : 's'} · private &amp; isolated</p>
          </div>
          <button onClick={lock} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white" data-testid="vault-lock-btn">
            <Unlock className="w-4 h-4" /> Lock
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-6 pb-4 -mt-1 flex flex-wrap gap-x-6 gap-y-1.5">
          <a href="/api/hub" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-300 hover:text-violet-200 transition-colors" data-testid="vault-materials-hub-link">
            <FileText className="w-3.5 h-3.5" /> Materials Hub — all 71 documents →
          </a>
          <a href="/api/journal" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-300 hover:text-violet-200 transition-colors" data-testid="vault-build-journal-link">
            <BookText className="w-3.5 h-3.5" /> Build Journal — dated build history →
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Add panel */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-8 shadow-sm" data-testid="vault-add-panel">
          <div className="flex gap-2 mb-4">
            {[['note', StickyNote, 'Note'], ['link', Link2, 'Link'], ['file', Upload, 'File']].map(([t, Icon, lbl]) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === t ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`vault-tab-${t}`}
              >
                <Icon className="w-4 h-4" /> {lbl}
              </button>
            ))}
          </div>

          {tab === 'note' && (
            <div className="space-y-3" data-testid="vault-note-form">
              <Input placeholder="Title" value={noteForm.title} onChange={e => setNoteForm({ ...noteForm, title: e.target.value })} data-testid="vault-note-title" />
              <Textarea placeholder="Write your note…" rows={4} value={noteForm.content} onChange={e => setNoteForm({ ...noteForm, content: e.target.value })} data-testid="vault-note-content" />
              <Input placeholder="Tags (comma-separated)" value={noteForm.tags} onChange={e => setNoteForm({ ...noteForm, tags: e.target.value })} data-testid="vault-note-tags" />
              <Button onClick={addNote} disabled={!noteForm.title.trim() || adding} className="bg-amber-600 hover:bg-amber-500" data-testid="vault-add-note-btn">
                <StickyNote className="w-4 h-4 mr-2" /> {adding ? 'Saving…' : 'Save Note'}
              </Button>
            </div>
          )}

          {tab === 'link' && (
            <div className="space-y-3" data-testid="vault-link-form">
              <Input placeholder="Title (e.g. 'Google Doc — Pitch')" value={linkForm.title} onChange={e => setLinkForm({ ...linkForm, title: e.target.value })} data-testid="vault-link-title" />
              <Input placeholder="https://…" value={linkForm.url} onChange={e => setLinkForm({ ...linkForm, url: e.target.value })} data-testid="vault-link-url" />
              <Input placeholder="Tags (comma-separated)" value={linkForm.tags} onChange={e => setLinkForm({ ...linkForm, tags: e.target.value })} data-testid="vault-link-tags" />
              <Button onClick={addLink} disabled={!linkForm.title.trim() || !linkForm.url.trim() || adding} className="bg-blue-600 hover:bg-blue-500" data-testid="vault-add-link-btn">
                <Link2 className="w-4 h-4 mr-2" /> {adding ? 'Saving…' : 'Save Link'}
              </Button>
            </div>
          )}

          {tab === 'file' && (
            <div className="space-y-3" data-testid="vault-file-form">
              <Input placeholder="Title (optional — defaults to filename)" value={fileForm.title} onChange={e => setFileForm({ ...fileForm, title: e.target.value })} data-testid="vault-file-title" />
              <input
                type="file"
                onChange={e => setFileObj(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:text-sm hover:file:bg-slate-700"
                data-testid="vault-file-input"
              />
              <Input placeholder="Tags (comma-separated)" value={fileForm.tags} onChange={e => setFileForm({ ...fileForm, tags: e.target.value })} data-testid="vault-file-tags" />
              <Button onClick={uploadFile} disabled={!fileObj || adding} className="bg-emerald-600 hover:bg-emerald-500" data-testid="vault-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> {adding ? 'Uploading…' : 'Upload File'}
              </Button>
            </div>
          )}
          {error && <p className="mt-3 text-xs text-red-600" data-testid="vault-error">{error}</p>}
        </div>

        {/* Items list */}
        <div className="space-y-3" data-testid="vault-items-list">
          {items.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-10" data-testid="vault-empty">Your vault is empty. Add a note, link, or file above.</p>
          )}
          {items.map(item => {
            const meta = TYPE_META[item.type] || TYPE_META.note;
            const Icon = meta.icon;
            return (
              <div key={item.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3 shadow-sm" data-testid={`vault-item-${item.id}`}>
                <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${meta.color}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 break-words">{item.title}</p>
                  {item.type === 'note' && item.content && (
                    <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words">{item.content}</p>
                  )}
                  {item.type === 'link' && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mt-1 break-all" data-testid={`vault-open-link-${item.id}`}>
                      {item.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  )}
                  {item.type === 'file' && (
                    <button onClick={() => openFile(item)} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 mt-1" data-testid={`vault-open-file-${item.id}`}>
                      <Download className="w-3 h-3" /> {item.original_filename} {item.size ? `· ${humanSize(item.size)}` : ''}
                    </button>
                  )}
                  {item.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {item.tags.map(t => (
                        <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <button onClick={() => removeItem(item)} className="text-slate-300 hover:text-red-500 transition-colors flex-shrink-0" title="Delete" data-testid={`vault-delete-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
